import React, {useState, useMemo} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useGetAllVideos, useGetVideoScoringStates} from '../../api/graphql_all_videos';
import {useGetAllAgents} from '../../api/graphql_agents';
import {
  useRescoreVideosAsync,
  useEstimateRescoreCost,
  useAdminResetAllVideoScores,
  useAdminResetAllVideoSummaries,
  usePreviewReviewSelector,
} from '../../api/graphql_admin_rescore';
import {useConfirm, useNotify} from '../ui/ConfirmDialog';
import {Link as RouterLink} from 'react-router-dom';
import {Video, Agent} from '../../types/generated/graphql';
import {usePageTitle} from '../../hooks/usePageTitle';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Pagination,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {VideoCard} from '../YouTube/VideoCard';
import {AgentPicker} from './AgentPicker';
import {
  pcolors,
  cardSx,
  pageSx,
  pageTitleSx,
  sectionLabelSx,
} from '../../theme/planapusTheme';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

export default function AdminVideos({client}: Props) {
  usePageTitle('Admin – Videos');

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  // Score-state filter. Backed by `getVideoScoringStates` which
  // returns booleans per videoId. Applied client-side after the main
  // video fetch. `channel-gated` covers videos whose only score is
  // the synthetic CHANNEL_GATE row — rating inherited from the parent
  // channel rather than produced by agents reading the video.
  type ScoreFilter =
    | 'all'
    | 'scored'
    | 'agent'
    | 'feedback'
    | 'channel-gated'
    | 'unscored';
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter>('all');
  // Age-band filter: when non-null, re-asks the backend for
  // `getAggregateScoreForVideos(..., childAge)` so each tile shows the
  // per-age-band verdict from the finalize row instead of the
  // universal rating. `null` == the universal family-agnostic view.
  const [filterChildAge, setFilterChildAge] = useState<number | null>(null);
  const [limit, setLimit] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;

  const {data, loading, error} = useGetAllVideos(client, limit);
  const {data: agentsData} = useGetAllAgents(client);
  const agents: Agent[] = agentsData?.getAgents ?? [];

  const [rescoreSampleSize, setRescoreSampleSize] = useState<number>(5);
  const [rescoreSkipExisting, setRescoreSkipExisting] = useState<boolean>(true);
  const [rescoreResetFirst, setRescoreResetFirst] = useState<boolean>(false);
  const [rescoreSelectedAgentKeys, setRescoreSelectedAgentKeys] = useState<string[]>([]);
  const [rescoreError, setRescoreError] = useState<string | null>(null);
  const [runRescoreAsync, {loading: rescoringAsync}] = useRescoreVideosAsync(client);
  const [rescoreJobId, setRescoreJobId] = useState<string | null>(null);

  // --- Rescore entire library ---------------------------------------------
  // Estimator + queue mutation for the "rescore everything" flow. Keeps
  // its own jobId so the two panels' success banners don't interfere.
  const [loadEstimate, estimateState] = useEstimateRescoreCost(client);
  const estimate = estimateState.data?.estimateRescoreCost;
  const [libraryJobId, setLibraryJobId] = useState<string | null>(null);
  const [libraryResetFirst, setLibraryResetFirst] = useState<boolean>(true);
  const [libraryUseSelector, setLibraryUseSelector] = useState<boolean>(false);
  // Cap on how many videos this run will score. Pulled from cache in
  // most-recent-first order (snapshot_date desc), so "1000" always
  // means the freshest 1000 candidates after filters apply.
  const [libraryMaxVideos, setLibraryMaxVideos] = useState<number>(1000);
  // When true (default), the bulk rescorer drops videos that already
  // have at least one agent_score row before slicing to the cap. The
  // toggle is meaningless when reset-before-rescoring is on, so the
  // UI disables it in that mode.
  const [libraryUnscoredOnly, setLibraryUnscoredOnly] = useState<boolean>(true);

  // Review-selector preview state — lets an admin sanity-check the
  // selector before wiring it into a rescore. Pool/sample sizes are
  // inputs; bucket counts + per-video reasons come back in `preview`.
  const [selectorPool, setSelectorPool] = useState<number>(60);
  const [selectorSample, setSelectorSample] = useState<number>(20);
  const [runSelectorPreview, selectorPreviewState] = usePreviewReviewSelector(client);
  const preview = selectorPreviewState.data?.previewReviewSelector;
  const [resetAllScores, {loading: resettingAll}] = useAdminResetAllVideoScores(client);
  const [resetAllSummaries, {loading: resettingSummaries}] =
    useAdminResetAllVideoSummaries(client);
  const confirm = useConfirm();
  const notify = useNotify();

  // Library-wide nuke — separate from the rescore button so an admin can
  // "wipe first, look around, rescore later" without triggering a job.
  const handleResetAllScores = async () => {
    const ok = await confirm({
      title: 'Reset ALL video scores?',
      message:
        'This will delete every AgentScore row across every video in the database. ' +
        'It cannot be undone. No rescoring will run — you\'ll need to kick off a ' +
        'library rescore separately. Proceed only if you really want a blank slate.',
      confirmLabel: 'Delete everything',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await resetAllScores();
      const r = res.data?.adminResetAllVideoScores;
      notify({
        severity: 'success',
        message: r
          ? `Deleted ${r.scoresDeleted.toLocaleString()} score row${r.scoresDeleted === 1 ? '' : 's'} across ${r.videosAffected.toLocaleString()} video${r.videosAffected === 1 ? '' : 's'}.`
          : 'Library reset complete.',
        durationMs: 8000,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Library reset failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  // Wipes the `video_summaries` collection so the next scoring pass
  // regenerates summaries with the current summarizer code. Admins
  // reach for this after a summarizer change (e.g. the data-provenance
  // header bump) where existing cached summaries are silently stale —
  // the new summarizer wouldn't run because `getOrCreate` hits the
  // cache first. Agent scores stay intact; they just keep referencing
  // their old cache_llm rows until rescored.
  const handleResetAllSummaries = async () => {
    const ok = await confirm({
      title: 'Reset ALL video summaries?',
      message:
        'This will delete every video_summaries row across every video in the database. ' +
        'Agent scores are NOT touched — they keep their current ratings until re-evaluated. ' +
        'The next scoring pass will regenerate summaries from scratch with the current ' +
        'summarizer code. Use this after a summarizer change when existing summaries are stale.',
      confirmLabel: 'Delete all summaries',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await resetAllSummaries();
      const r = res.data?.adminResetAllVideoSummaries;
      notify({
        severity: 'success',
        message: r
          ? `Deleted ${r.summariesDeleted.toLocaleString()} summary row${r.summariesDeleted === 1 ? '' : 's'} across ${r.videosAffected.toLocaleString()} video${r.videosAffected === 1 ? '' : 's'}.`
          : 'Summary reset complete.',
        durationMs: 8000,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Summary reset failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const agentKey = (a: {specialization: string; version: string}) =>
    `${a.specialization}|${a.version}`;

  const buildSelectedAgents = () =>
    rescoreSelectedAgentKeys.length === 0
      ? null
      : rescoreSelectedAgentKeys.map(k => {
          const [specialization, version] = k.split('|');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return {specialization: specialization as any, version};
        });

  const handleRunRescoreAsync = async () => {
    setRescoreError(null);
    setRescoreJobId(null);
    try {
      const res = await runRescoreAsync({
        variables: {
          input: {
            sampleSize: rescoreSampleSize,
            agents: buildSelectedAgents(),
            skipExisting: rescoreSkipExisting,
            resetFirst: rescoreResetFirst,
          },
        },
      });
      setRescoreJobId(res.data?.rescoreVideosAsync?.jobId ?? '');
    } catch (e) {
      setRescoreError(e instanceof Error ? e.message : String(e));
    }
  };

  // --- Rescore entire library handlers -------------------------------------
  const handleEstimateLibrary = () => {
    // Mirror the rescore knobs so the preview reflects the slice the
    // worker will actually score (cap + unscored filter). The toggle
    // is moot when reset-first is on, so we suppress it in that case.
    const effectiveUnscoredOnly = libraryUnscoredOnly && !libraryResetFirst;
    loadEstimate({
      variables: {
        input: {
          sampleSize: libraryMaxVideos,
          unscoredOnly: effectiveUnscoredOnly,
        },
      },
    });
  };

  const handleRunLibraryRescore = async () => {
    if (!estimate) return;
    const fmt = `$${estimate.estimatedCostUSD.toFixed(4)}`;
    const ok = await confirm({
      title: 'Rescore the entire library?',
      message: `Estimated spend: ${fmt} across ${estimate.videoCount} videos ` +
        `(${estimate.channelGatedCount} expected to skip via channel gate). ` +
        (libraryResetFirst ? 'ALL existing scores will be wiped first. ' : '') +
        'This enqueues a single bulk job — progress lives on Admin → Jobs.',
      confirmLabel: 'Run',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await runRescoreAsync({
        variables: {
          input: {
            // The admin-chosen cap. Cache returns videos most-recent
            // first (snapshot_date desc) so this always lands on the
            // freshest candidates. When `unscoredOnly` is on the worker
            // oversamples and trims, so the slice still hits exactly
            // `sampleSize` unscored videos.
            sampleSize: libraryMaxVideos,
            videoIds: null,
            agents: null,
            skipExisting: !libraryResetFirst,
            resetFirst: libraryResetFirst,
            // Only meaningful when we're not also resetting — if we
            // wipe everything first, nothing is "already scored".
            unscoredOnly: libraryUnscoredOnly && !libraryResetFirst,
            useReviewSelector: libraryUseSelector,
          },
        },
      });
      const jobId = res.data?.rescoreVideosAsync?.jobId ?? null;
      setLibraryJobId(jobId);
      notify({
        severity: 'success',
        message: jobId ? `Library rescore queued as job #${jobId}.` : 'Library rescore queued.',
        action: jobId ? {label: 'View job', href: `#/admin/operations/tasks?jobId=${jobId}`} : undefined,
        durationMs: 10_000,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Failed to queue library rescore: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  // Score-state lookup for the currently loaded page. We request flags
  // for every video returned by `getAllVideos`; the backend batches
  // Firestore `in` queries so this stays cheap. Only fires once the
  // main list has ids to look up.
  const allVideoIds = useMemo(() => (data?.getAllVideos ?? []).map(v => v.id), [data]);
  const {data: scoringData} = useGetVideoScoringStates(client, allVideoIds);
  const scoringById = useMemo(() => {
    const m = new Map<
      string,
      {hasAgentScore: boolean; hasFeedback: boolean; inheritedFromChannel: boolean}
    >();
    for (const s of scoringData?.getVideoScoringStates ?? []) {
      m.set(s.videoId, {
        hasAgentScore: s.hasAgentScore,
        hasFeedback: s.hasFeedback,
        inheritedFromChannel: s.inheritedFromChannel,
      });
    }
    return m;
  }, [scoringData]);

  const matchesScoreFilter = (videoId: string): boolean => {
    if (scoreFilter === 'all') return true;
    // While the scoring-state query is in flight, keep every video
    // visible — filtering-to-zero on an empty dataset looks broken.
    const s = scoringById.get(videoId);
    if (!s) return true;
    switch (scoreFilter) {
      case 'scored':
        return s.hasAgentScore || s.hasFeedback;
      case 'agent':
        return s.hasAgentScore;
      case 'feedback':
        return s.hasFeedback;
      case 'channel-gated':
        return s.inheritedFromChannel;
      case 'unscored':
        return !s.hasAgentScore && !s.hasFeedback;
    }
  };

  const filteredAndSortedVideos = useMemo(() => {
    if (!data?.getAllVideos) return [];
    let result = [...data.getAllVideos];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(
        v =>
          v.title.toLowerCase().includes(s) ||
          v.author.name.toLowerCase().includes(s) ||
          v.description.toLowerCase().includes(s)
      );
    }
    result = result.filter(v => matchesScoreFilter(v.id));
    switch (sortBy) {
      case 'date':
        result.sort((a, b) => {
          if (!a.published || !b.published) return 0;
          return new Date(b.published).getTime() - new Date(a.published).getTime();
        });
        break;
      case 'title':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'author':
        result.sort((a, b) => a.author.name.localeCompare(b.author.name));
        break;
      case 'views':
        result.sort((a, b) => (b.view_count ?? 0) - (a.view_count ?? 0));
        break;
    }
    return result;
  }, [data, searchTerm, sortBy, scoreFilter, scoringById]);

  const paginatedVideos = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedVideos.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedVideos, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedVideos.length / itemsPerPage);

  if (loading) {
    return (
      <Box sx={pageSx}>
        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8}}>
          <CircularProgress size={24} />
          <Typography sx={{ml: 2, fontSize: 13, color: pcolors.textSecondary}}>Loading videos…</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageSx}>
        <Paper sx={{...cardSx, p: 2, borderLeft: `3px solid ${pcolors.red[500]}`}}>
          <Typography sx={{fontSize: 14, fontWeight: 600, color: pcolors.red[700]}}>Error loading videos</Typography>
          <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 0.5}}>{error.message}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Box sx={{mb: 2}}>
        <Typography sx={pageTitleSx}>Videos</Typography>
        <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.5}}>
          All videos cached across every profile. Click a card to inspect scores and transcripts.
        </Typography>
      </Box>

      <Paper sx={{...cardSx, p: 2, mb: 2}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
          <ScienceIcon sx={{fontSize: 18, color: pcolors.textSecondary}} />
          <Typography sx={{fontSize: 14, fontWeight: 600}}>Rescore videos</Typography>
          <Typography sx={{fontSize: 12, color: pcolors.textMuted, ml: 1}}>
            Sweep cached videos with selected agents. Run a small sample first to preview cost, then scale up.
          </Typography>
        </Box>

        <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
          <TextField
            size="small"
            type="number"
            label="Sample size"
            value={rescoreSampleSize}
            onChange={e => setRescoreSampleSize(Math.max(1, Number(e.target.value) || 1))}
            sx={{width: 140}}
            inputProps={{min: 1}}
          />

          <Box sx={{minWidth: 320, flex: 1, maxWidth: 560}}>
            <AgentPicker
              agents={agents}
              value={rescoreSelectedAgentKeys}
              onChange={setRescoreSelectedAgentKeys}
              label={`Agents (${rescoreSelectedAgentKeys.length || 'all'})`}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={rescoreSkipExisting}
                onChange={e => setRescoreSkipExisting(e.target.checked)}
                disabled={rescoreResetFirst}
              />
            }
            label={
              <Typography sx={{fontSize: 13}}>
                {rescoreSkipExisting ? 'Skip already scored' : 'Force rescore all'}
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                color="error"
                checked={rescoreResetFirst}
                onChange={e => setRescoreResetFirst(e.target.checked)}
              />
            }
            label={
              <Typography sx={{fontSize: 13}} title="Wipes every existing AgentScore for each sampled video before scoring. Use when switching pipelines so legacy rows don't mix with new gated-pipeline rows.">
                Reset before rescoring
              </Typography>
            }
          />

          <Button
            variant="contained"
            startIcon={rescoringAsync ? <CircularProgress size={14} /> : <PlayArrowIcon />}
            disabled={rescoringAsync}
            onClick={handleRunRescoreAsync}
            title="Enqueues as a BullMQ background job. Progress, logs, and cancel are available on the Tasks & jobs page."
          >
            {rescoringAsync ? 'Queuing…' : 'Run rescore'}
          </Button>
        </Box>

        {rescoreError && (
          <Alert severity="error" sx={{mt: 2}}>
            {rescoreError}
          </Alert>
        )}

        {rescoreJobId !== null && (
          <Alert severity="success" sx={{mt: 2}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
              <span>Rescore job enqueued{rescoreJobId ? ` as #${rescoreJobId}` : ''}.</span>
              <RouterLink
                to={rescoreJobId ? `/admin/operations/tasks?jobId=${rescoreJobId}` : '/admin/operations/tasks'}
                style={{color: 'inherit', textDecoration: 'underline'}}
              >
                {rescoreJobId ? 'View job →' : 'Open Tasks & jobs →'}
              </RouterLink>
            </Box>
          </Alert>
        )}
      </Paper>

      {/* ============================================================
          Rescore the entire library — with a cost preview so admins
          see $X estimated before committing. Respects the channel gate
          (videos on conclusively-restricted channels don't LLM-cost
          anything and get subtracted from the estimate).
          ============================================================ */}
      <Paper sx={{...cardSx, p: 2, mb: 2}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
          <Typography sx={{fontSize: 14, fontWeight: 600}}>Rescore entire library</Typography>
          <Typography sx={{fontSize: 11, color: pcolors.textSecondary}}>
            Runs the gated pipeline across every cached video. Channel-gated videos are skipped.
          </Typography>
        </Box>

        <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap'}}>
          <TextField
            label="Max videos"
            type="number"
            size="small"
            value={libraryMaxVideos}
            onChange={e => {
              const n = Number.parseInt(e.target.value, 10);
              if (Number.isFinite(n) && n > 0) {
                setLibraryMaxVideos(Math.min(10_000, n));
              }
            }}
            inputProps={{min: 1, max: 10_000, step: 100}}
            helperText="Most-recent first"
            sx={{width: 140}}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="error"
                checked={libraryResetFirst}
                onChange={e => setLibraryResetFirst(e.target.checked)}
              />
            }
            label={
              <Typography sx={{fontSize: 13}}>
                Reset before rescoring
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={libraryUnscoredOnly && !libraryResetFirst}
                disabled={libraryResetFirst}
                onChange={e => setLibraryUnscoredOnly(e.target.checked)}
              />
            }
            label={
              <Typography sx={{fontSize: 13}} title="Skip videos that already have at least one agent score. Disabled when 'Reset before rescoring' is on, since reset wipes every score anyway.">
                Skip already-scored videos
              </Typography>
            }
          />
          <Button
            variant="outlined"
            size="small"
            disabled={estimateState.loading}
            onClick={handleEstimateLibrary}
            startIcon={estimateState.loading ? <CircularProgress size={14} /> : undefined}
          >
            {estimateState.loading ? 'Estimating…' : 'Estimate cost'}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={!estimate || rescoringAsync}
            onClick={handleRunLibraryRescore}
          >
            {rescoringAsync ? 'Queuing…' : 'Run library rescore'}
          </Button>
          <Box sx={{flex: 1}} />
          <Button
            variant="outlined"
            size="small"
            color="warning"
            disabled={resettingSummaries}
            onClick={handleResetAllSummaries}
            title="Delete every video_summaries row so the next scoring pass regenerates summaries with the current summarizer code. Agent scores are NOT touched."
            sx={{mr: 1}}
          >
            {resettingSummaries ? 'Resetting…' : 'Reset all summaries'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            disabled={resettingAll}
            onClick={handleResetAllScores}
            title="Delete every AgentScore row in the database. No rescoring — just wipes."
          >
            {resettingAll ? 'Resetting…' : 'Reset all scores'}
          </Button>
        </Box>

        <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 1}}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={libraryUseSelector}
                onChange={e => setLibraryUseSelector(e.target.checked)}
              />
            }
            label={
              <Typography sx={{fontSize: 13}} title="Route each video through the review-selector LLM (metadata-only) before scoring. The full pool gets bucketed TOP / MID / BOTTOM, then sampled with a top/bottom bias so scoring budget lands on the extremes.">
                Use review-selector (sample extremes only)
              </Typography>
            }
          />
          {libraryUseSelector && (
            <Typography sx={{fontSize: 11, color: pcolors.textMuted}}>
              selector pool defaults to 4× sampleSize; bias 0.4 TOP / 0.4 BOTTOM / 0.2 MID
            </Typography>
          )}
        </Box>

        {estimateState.error && (
          <Alert severity="error" sx={{mt: 2}}>
            {estimateState.error.message}
          </Alert>
        )}

        {estimate && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: pcolors.bg,
              border: `1px solid ${pcolors.borderLight}`,
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap'}}>
              <Typography
                sx={{fontSize: 22, fontWeight: 800, color: pcolors.green[700], fontVariantNumeric: 'tabular-nums'}}
              >
                ${estimate.estimatedCostUSD.toFixed(4)}
              </Typography>
              <Typography sx={{fontSize: 12, color: pcolors.textSecondary}}>
                estimated · {estimate.videoCount.toLocaleString()} video{estimate.videoCount === 1 ? '' : 's'}
                {estimate.channelGatedCount > 0 && ` · ${estimate.channelGatedCount.toLocaleString()} channel-gated (skipped)`}
                {' · '}
                {estimate.llmCallCount.toLocaleString()} LLM calls
              </Typography>
              <Typography sx={{fontSize: 10, color: pcolors.textMuted, ml: 'auto'}}>
                averages drawn from last {estimate.sampleSize} scores · {new Date(estimate.generatedAt).toLocaleTimeString()}
              </Typography>
            </Box>
            <Box sx={{mt: 1.5, display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(4, 1fr)'}, gap: 1}}>
              {estimate.breakdown.map(row => (
                <Box
                  key={row.stage}
                  sx={{
                    p: 1,
                    bgcolor: pcolors.card,
                    border: `1px solid ${pcolors.borderLight}`,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    sx={{fontSize: 10, fontWeight: 600, color: pcolors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4}}
                  >
                    {row.stage}
                  </Typography>
                  <Typography sx={{fontSize: 13, fontWeight: 700, color: pcolors.textPrimary, fontVariantNumeric: 'tabular-nums', mt: 0.25}}>
                    ${row.estimatedCostUSD.toFixed(4)}
                  </Typography>
                  <Typography sx={{fontSize: 10, color: pcolors.textSecondary, mt: 0.25}}>
                    {row.callsPerVideo.toFixed(1)} call{row.callsPerVideo === 1 ? '' : 's'}/video · ${row.avgCostUSD.toFixed(5)} avg
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {libraryJobId && (
          <Alert severity="success" sx={{mt: 2}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
              <span>Library rescore queued as #{libraryJobId}.</span>
              <RouterLink
                to={`/admin/operations/tasks?jobId=${libraryJobId}`}
                style={{color: 'inherit', textDecoration: 'underline'}}
              >
                View job →
              </RouterLink>
            </Box>
          </Alert>
        )}
      </Paper>

      {/* =============================================================
          Review-selector preview — runs the TOP/MID/BOTTOM bucketing
          LLM over a pool of videos so admins can see its decisions
          before wiring it into a rescore. Cheap (metadata-only).
          ============================================================= */}
      <Paper sx={{...cardSx, p: 2, mb: 2}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
          <Typography sx={{fontSize: 14, fontWeight: 600}}>Review selector · preview</Typography>
          <Typography sx={{fontSize: 11, color: pcolors.textSecondary}}>
            Metadata-only LLM that buckets videos TOP / MID / BOTTOM so rescoring spends budget on extremes.
          </Typography>
        </Box>

        <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap'}}>
          <TextField
            size="small"
            label="Pool size"
            type="number"
            value={selectorPool}
            onChange={e => setSelectorPool(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            sx={{width: 120}}
          />
          <TextField
            size="small"
            label="Sample size"
            type="number"
            value={selectorSample}
            onChange={e => setSelectorSample(Math.max(1, Math.min(selectorPool, Number(e.target.value) || 1)))}
            sx={{width: 120}}
          />
          <Button
            size="small"
            variant="contained"
            disabled={selectorPreviewState.loading}
            onClick={() =>
              runSelectorPreview({
                variables: {
                  input: {
                    poolSize: selectorPool,
                    sampleSize: selectorSample,
                    topBias: 0.4,
                    bottomBias: 0.4,
                  },
                },
              })
            }
            startIcon={selectorPreviewState.loading ? <CircularProgress size={14} /> : undefined}
          >
            {selectorPreviewState.loading ? 'Classifying…' : 'Run preview'}
          </Button>
        </Box>

        {selectorPreviewState.error && (
          <Alert severity="error" sx={{mt: 2}}>
            {selectorPreviewState.error.message}
          </Alert>
        )}

        {preview && (
          <Box sx={{mt: 2}}>
            <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1.25}}>
              <Typography sx={{fontSize: 12, color: pcolors.textSecondary}}>
                Pooled <b>{preview.poolSize}</b> · Sampled <b>{preview.sampleSize}</b>
                {preview.estimatedCostUSD != null && ` · $${preview.estimatedCostUSD.toFixed(4)}`}
                {' · '}
                {preview.tokenUsage.total.toLocaleString()} tokens
                {' · '}
                {(preview.durationMs / 1000).toFixed(1)}s
              </Typography>
              <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                <Chip size="small" label={`TOP ${preview.topCount}`} sx={{fontSize: 11, bgcolor: pcolors.green[50], color: pcolors.green[700], fontWeight: 700}} />
                <Chip size="small" label={`MID ${preview.midCount}`} sx={{fontSize: 11, bgcolor: pcolors.borderLight, color: pcolors.textSecondary, fontWeight: 700}} />
                <Chip size="small" label={`BOTTOM ${preview.bottomCount}`} sx={{fontSize: 11, bgcolor: pcolors.red[50], color: pcolors.red[700], fontWeight: 700}} />
              </Box>
            </Box>
            <Box
              sx={{
                maxHeight: 360,
                overflow: 'auto',
                border: `1px solid ${pcolors.borderLight}`,
                borderRadius: 1,
              }}
            >
              {preview.classifications.map(c => (
                <Box
                  key={c.videoId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '72px 24px 1fr 80px',
                    gap: 1,
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderBottom: `1px solid ${pcolors.borderLight}`,
                    bgcolor: c.selected ? pcolors.blue[50] : 'transparent',
                    '&:last-of-type': {borderBottom: 'none'},
                  }}
                >
                  <Chip
                    size="small"
                    label={c.bucket}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor:
                        c.bucket === 'TOP'
                          ? pcolors.green[50]
                          : c.bucket === 'BOTTOM'
                            ? pcolors.red[50]
                            : pcolors.borderLight,
                      color:
                        c.bucket === 'TOP'
                          ? pcolors.green[700]
                          : c.bucket === 'BOTTOM'
                            ? pcolors.red[700]
                            : pcolors.textSecondary,
                    }}
                  />
                  <Typography sx={{fontSize: 11, color: pcolors.textMuted, textAlign: 'center'}}>
                    {c.selected ? '✓' : ''}
                  </Typography>
                  <Box sx={{minWidth: 0}}>
                    <Typography sx={{fontSize: 12, fontWeight: 500, color: pcolors.textPrimary}} noWrap>
                      {c.title || c.videoId}
                    </Typography>
                    <Typography sx={{fontSize: 11, color: pcolors.textSecondary}} noWrap>
                      {c.channelName || '—'} · {c.reason}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{fontSize: 11, color: pcolors.textMuted, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}
                  >
                    {(c.confidence * 100).toFixed(0)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      <Paper sx={{...cardSx, p: 2, mb: 2}}>
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
          <TextField
            size="small"
            placeholder="Search title, author, description…"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{flex: 1, minWidth: 220}}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{fontSize: 18, color: pcolors.textMuted}} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{minWidth: 160}}>
            <InputLabel>Sort</InputLabel>
            <Select value={sortBy} label="Sort" onChange={(e: SelectChangeEvent) => setSortBy(e.target.value)}>
              <MenuItem value="date" sx={{fontSize: 13}}>Newest</MenuItem>
              <MenuItem value="title" sx={{fontSize: 13}}>Title A–Z</MenuItem>
              <MenuItem value="author" sx={{fontSize: 13}}>Author A–Z</MenuItem>
              <MenuItem value="views" sx={{fontSize: 13}}>Most viewed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{minWidth: 170}}>
            <InputLabel>Scored</InputLabel>
            <Select
              value={scoreFilter}
              label="Scored"
              onChange={(e: SelectChangeEvent) => {
                setScoreFilter(e.target.value as ScoreFilter);
                setCurrentPage(1);
              }}
            >
              <MenuItem value="all" sx={{fontSize: 13}}>All</MenuItem>
              <MenuItem value="scored" sx={{fontSize: 13}}>Scored (agent or feedback)</MenuItem>
              <MenuItem value="agent" sx={{fontSize: 13}}>Agent scored</MenuItem>
              <MenuItem value="feedback" sx={{fontSize: 13}}>Feedback scored</MenuItem>
              <MenuItem value="channel-gated" sx={{fontSize: 13}}>Channel-inherited</MenuItem>
              <MenuItem value="unscored" sx={{fontSize: 13}}>Not scored</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{minWidth: 120}}>
            <InputLabel>Limit</InputLabel>
            <Select value={limit.toString()} label="Limit" onChange={e => setLimit(Number(e.target.value))}>
              {[50, 100, 200, 500, 1000, 2000, 5000, 10000].map(n => (
                <MenuItem key={n} value={n} sx={{fontSize: 13}}>{n.toLocaleString()}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/*
            Age-band filter. Dropdown values are the band midpoint age
            (3/7/11/16) — the backend's pickAgeBandVerdict() is keyed on
            a single age and looks up the band containing it. When set,
            every tile re-fetches its aggregate via a childAge-scoped
            loader and shows that band's rating + score from the
            finalize row. Videos without a finalize row (legacy /
            short-circuit / channel-gated) fall back to the universal
            rating — same as today.
          */}
          <FormControl size="small" sx={{minWidth: 170}}>
            <InputLabel>Age</InputLabel>
            <Select
              value={filterChildAge == null ? 'all' : String(filterChildAge)}
              label="Age"
              onChange={(e: SelectChangeEvent) => {
                const v = e.target.value;
                setFilterChildAge(v === 'all' ? null : Number(v));
              }}
            >
              <MenuItem value="all" sx={{fontSize: 13}}>All ages (universal)</MenuItem>
              <MenuItem value="3" sx={{fontSize: 13}}>Toddler · 2–5</MenuItem>
              <MenuItem value="7" sx={{fontSize: 13}}>Kid · 6–9</MenuItem>
              <MenuItem value="11" sx={{fontSize: 13}}>Tween · 10–13</MenuItem>
              <MenuItem value="16" sx={{fontSize: 13}}>Teen · 14–18</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{flex: 'none'}}>
            <Typography sx={{...sectionLabelSx, display: 'block'}}>Results</Typography>
            <Typography sx={{fontSize: 16, fontWeight: 600, color: pcolors.textPrimary, fontVariantNumeric: 'tabular-nums'}}>
              {filteredAndSortedVideos.length}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {paginatedVideos.length > 0 ? (
        <>
          <Grid container spacing={{xs: 2, md: 2}}>
            {paginatedVideos.map((video: Video) => {
              const s = scoringById.get(video.id);
              return (
                <Grid key={video.id} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
                  <Box sx={{position: 'relative'}}>
                    <VideoCard video={video} client={client} childAge={filterChildAge} />
                    {/* Channel-inherited marker — overlays the tile so
                        you can spot these in the default "All" view
                        without switching to the dedicated filter. */}
                    {s?.inheritedFromChannel && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: pcolors.red[50],
                          color: pcolors.red[700],
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          textTransform: 'uppercase',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                          pointerEvents: 'none',
                        }}
                      >
                        channel-gated
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{display: 'flex', justifyContent: 'center', mt: 3}}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, v) => setCurrentPage(v)}
              color="primary"
              size="small"
            />
          </Box>
        </>
      ) : (
        <Paper sx={{...cardSx, p: 5, textAlign: 'center'}}>
          <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>
            No videos match your search.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
